/**
 * Route guard: renders children only if we have an auth session; otherwise
 * redirects to /login. Runs the redirect in useEffect so it happens after
 * mount, avoiding "cannot update during render" warnings.
 *
 * A session alone is not enough: the session lives in localStorage and the
 * family key in IndexedDB, so a browser can keep one and lose the other.
 * Signed in with no family key on this browser, nothing can be decrypted —
 * send the parent to the restore screen instead of a dashboard full of
 * alerts stuck in DECRYPTING.
 */

import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { AppShell } from '../components/AppShell';
import {
  familyKeyHydrated,
  familyKeyInMemory,
  isAuthed,
} from '../state/session';

interface Props {
  children: ComponentChildren;
}

export function Guard({ children }: Props) {
  const location = useLocation();
  const authed = isAuthed.value;
  const keyMissing =
    familyKeyHydrated.value && familyKeyInMemory.value === null;

  useEffect(() => {
    if (!authed) {
      location.route('/login', true);
    } else if (keyMissing) {
      location.route('/login/restore-mnemonic', true);
    }
  }, [authed, keyMissing, location]);

  if (!authed || keyMissing) return null;
  return <AppShell>{children}</AppShell>;
}
